import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import SurebetService from '../services/SurebetService';
import { getMaxMargin } from '../utils/surebetUtils';
import useSurebetViewFilters from '../viewFilters/useSurebetViewFilters';
import { DEFAULT_SUREBET_VIEW_FILTERS } from '../viewFilters/surebetViewFilters';

/**
 * SurebetContext - Context for managing surebet data and state
 * Handles data fetching, refresh management, and real-time updates
 * Based on MarketsAnalysisDto structure from backend
 *
 * Client-side view filters (search / minMargin / championships / markets / time)
 * live here via useSurebetViewFilters and persist under arbiwin_view_filters.
 * They are independent from API bookmaker recalculation (FilterContext).
 */

// Default surebet state structure
const DEFAULT_SUREBET_STATE = {
  // Data state
  surebets: [], // Array of MarketsAnalysisDto objects
  filteredSurebets: [],
  viewFilters: DEFAULT_SUREBET_VIEW_FILTERS,
  filteredCount: 0,
  hasActiveViewFilters: false,
  loading: false,
  error: null,
  
  // Refresh state
  lastRefresh: null,
  isRefreshing: false,
  autoRefreshEnabled: true,
  refreshInterval: 600000, // 10 minutes in milliseconds
  
  // Filter state
  selectedBookmakers: [],
  sortBy: 'margin', // 'margin', 'date', 'time'
  sortOrder: 'desc', // 'asc', 'desc'
  
  // Statistics
  totalSurebets: 0,
  profitableSurebets: 0,
  averageMargin: 0,
};

// Create the context
const SurebetContext = createContext(DEFAULT_SUREBET_STATE);

/**
 * SurebetProvider - Provider component for surebet context
 * Manages surebet data, refresh logic, and state updates
 */
export const SurebetProvider = ({ children, mode = 'arbitrage' }) => {
  const isNearArb = mode === 'nearArb';

  // Data state
  const [surebets, setSurebets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nearArbMeta, setNearArbMeta] = useState(null);
  
  // Refresh state
  const [lastRefresh, setLastRefresh] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const refreshInterval = 600000; // 10 minutes
  
  // Filter state (API bookmaker selection — separate from view filters)
  const [selectedBookmakers, setSelectedBookmakers] = useState([]);
  const [sortBy, setSortBy] = useState('margin');
  const [sortOrder, setSortOrder] = useState('desc');

  const {
    viewFilters,
    filteredSurebets,
    filteredCount,
    hasActiveViewFilters,
    setViewFilters,
    setMinMargin,
    setSearchQuery,
    setSelectedChampionships,
    setSelectedMarketKeys,
    setTimeWindow,
    resetViewFilters
  } = useSurebetViewFilters(surebets);
  
  /**
   * Sort surebets by specified criteria
   */
  const sortSurebets = useCallback((surebetsToSort, sortByValue, sortOrderValue) => {
    if (!surebetsToSort || surebetsToSort.length === 0) {
      return [];
    }
    
    const sorted = [...surebetsToSort];
    
    switch (sortByValue) {
      case 'margin': {
        // Sort by highest margin value
        sorted.sort((a, b) => {
          const aMaxMargin = getMaxMargin(a);
          const bMaxMargin = getMaxMargin(b);
          return sortOrderValue === 'desc' ? bMaxMargin - aMaxMargin : aMaxMargin - bMaxMargin;
        });
        break;
      }
      
      case 'date': {
        // Sort by event date
        sorted.sort((a, b) => {
          const aDate = new Date(`${a.event.date} ${a.event.time}`);
          const bDate = new Date(`${b.event.date} ${b.event.time}`);
          return sortOrderValue === 'desc' ? bDate - aDate : aDate - bDate;
        });
        break;
      }
      
      case 'time': {
        // Sort by event time
        sorted.sort((a, b) => {
          const aTime = a.event.time;
          const bTime = b.event.time;
          return sortOrderValue === 'desc' 
            ? bTime.localeCompare(aTime) 
            : aTime.localeCompare(bTime);
        });
        break;
      }
      
      default:
        break;
    }
    
    return sorted;
  }, []);
  
  /**
   * Calculate statistics from current surebets
   */
  const statistics = useMemo(() => {
    const totalEvents = surebets.length;
    
    // Count total individual surebets (margin types) across all events
    let totalSurebets = 0;
    let profitableSurebets = 0;
    let totalMargin = 0;
    
    surebets.forEach(surebet => {
      // Count all margin types in this event
      const marginTypes = Object.keys(surebet.margins || {});
      totalSurebets += marginTypes.length;
      
      // Count profitable margin types (margin.value > 0)
      marginTypes.forEach(marginKey => {
        const margin = surebet.margins[marginKey];
        if (margin && margin.value > 0) {
          profitableSurebets++;
          totalMargin += margin.value;
        }
      });
    });
    
    const averageMargin = totalSurebets > 0 ? totalMargin / totalSurebets : 0;
    
    return {
      totalEvents, // Total number of events
      totalSurebets, // Total number of individual surebets (margin types)
      profitableSurebets, // Total number of profitable surebets (margin types)
      averageMargin: parseFloat(averageMargin.toFixed(2)),
    };
  }, [surebets]);
  
  /**
   * Load surebets data from API
   */
  const loadSurebets = useCallback(async (bookmakers = []) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = isNearArb
        ? await SurebetService.getNearArbAnalysis()
        : await SurebetService.getMarketsAnalysis({
          bookmakers,
          profitableOnly: true,
          useCache: false, // Don't use cache for manual loads
        });
      
      if (response.error) {
        if (isNearArb) {
          setNearArbMeta(response.meta || null);
          setSurebets([]);
          setError(response.error);
          setLoading(false);
          return { data: null, error: response.error };
        }
        throw new Error(response.error);
      }
      
      if (isNearArb) {
        setNearArbMeta(response.meta || null);
      }
      // Sort the data before setting state
      const sortedData = sortSurebets(response.data || [], sortBy, sortOrder);
      setSurebets(sortedData);
      setLastRefresh(new Date());
      setLoading(false);
      
      return { data: sortedData, error: null };
    } catch (err) {
      const errorMessage = err.message || 'Failed to load surebet data';
      setError(errorMessage);
      setLoading(false);
      
      return { data: null, error: errorMessage };
    }
  }, [isNearArb, sortSurebets, sortBy, sortOrder]);
  
  /**
   * Refresh surebets data manually
   */
  const refreshSurebets = useCallback(async () => {
    if (isRefreshing || loading) {
      return { data: null, error: 'Refresh already in progress' };
    }
    
    setIsRefreshing(true);
    const result = await loadSurebets(selectedBookmakers);
    setIsRefreshing(false);
    
    return result;
  }, [isRefreshing, loading, loadSurebets, selectedBookmakers]);
  
  /**
   * Update selected bookmakers for filtering
   */
  const updateBookmakers = useCallback((bookmakers) => {
    setSelectedBookmakers(bookmakers);
  }, []);
  
  /**
   * Update sort criteria
   */
  const updateSort = useCallback((newSortBy, newSortOrder = sortOrder) => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    
    // Re-sort existing data
    const sortedData = sortSurebets(surebets, newSortBy, newSortOrder);
    setSurebets(sortedData);
  }, [sortOrder, sortSurebets, surebets]);
  
  /**
   * Toggle auto-refresh
   */
  const toggleAutoRefresh = useCallback(() => {
    setAutoRefreshEnabled(prev => !prev);
  }, []);
  
  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);
  
  /**
   * Auto-refresh effect
   * Refreshes data every 10 minutes if auto-refresh is enabled
   */
  useEffect(() => {
    if (!autoRefreshEnabled) {
      return;
    }
    
    // Set up auto-refresh interval
    const intervalId = setInterval(() => {
      if (!isRefreshing && !loading) {
        loadSurebets(selectedBookmakers);
      }
    }, refreshInterval);
    
    // Clean up interval on unmount or when dependencies change
    return () => {
      clearInterval(intervalId);
    };
  }, [autoRefreshEnabled, isRefreshing, loading, loadSurebets, selectedBookmakers, refreshInterval]);
  
  /**
   * Load when the provider mounts and when the tab mode changes.
   * Clears the previous list first so arbitrage rows are not shown as near-arb.
   */
  useEffect(() => {
    setSurebets([]);
    setNearArbMeta(null);
    setError(null);
    loadSurebets(selectedBookmakers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNearArb]);
  
  // Context value
  const value = useMemo(() => ({
    // Data state
    mode,
    nearArbMeta,
    surebets,
    filteredSurebets,
    viewFilters,
    filteredCount,
    hasActiveViewFilters,
    loading,
    error,
    
    // Refresh state
    lastRefresh,
    isRefreshing,
    autoRefreshEnabled,
    refreshInterval,
    
    // Filter state (API bookmakers)
    selectedBookmakers,
    sortBy,
    sortOrder,
    
    // Statistics
    ...statistics,
    
    // Actions
    loadSurebets,
    refreshSurebets,
    updateBookmakers,
    updateSort,
    toggleAutoRefresh,
    clearError,

    // View-filter actions (client-side)
    setViewFilters,
    setMinMargin,
    setSearchQuery,
    setSelectedChampionships,
    setSelectedMarketKeys,
    setTimeWindow,
    resetViewFilters
  }), [
    mode,
    nearArbMeta,
    surebets,
    filteredSurebets,
    viewFilters,
    filteredCount,
    hasActiveViewFilters,
    loading,
    error,
    lastRefresh,
    isRefreshing,
    autoRefreshEnabled,
    selectedBookmakers,
    sortBy,
    sortOrder,
    statistics,
    loadSurebets,
    refreshSurebets,
    updateBookmakers,
    updateSort,
    toggleAutoRefresh,
    clearError,
    setViewFilters,
    setMinMargin,
    setSearchQuery,
    setSelectedChampionships,
    setSelectedMarketKeys,
    setTimeWindow,
    resetViewFilters
  ]);
  
  return (
    <SurebetContext.Provider value={value}>
      {children}
    </SurebetContext.Provider>
  );
};

SurebetProvider.propTypes = {
  children: PropTypes.node.isRequired,
  mode: PropTypes.oneOf(['arbitrage', 'nearArb']),
};

/**
 * useSurebet - Custom hook to access surebet context
 * Throws error if used outside of SurebetProvider
 */
export const useSurebet = () => {
  const context = useContext(SurebetContext);
  
  if (!context) {
    throw new Error('useSurebet must be used within a SurebetProvider');
  }
  
  return context;
};

export default SurebetContext;

